import {
	ButtonDropdownField,
	DropdownList,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Modal,
	Separator,
	Text,
} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import type {DeleteCustomFieldDropdownOptionConfirmationModal_viewer$data as DeleteCustomFieldDropdownOptionConfirmationModal_viewer} from './__generated__/DeleteCustomFieldDropdownOptionConfirmationModal_viewer.graphql';
import {UnsavedDropdownOption} from './hooks/useDropdownOptions';
import {DropdownOption} from './types';

type DeleteCustomFieldDropdownOptionConfirmationModalProps = ModalProps<
	{
		viewer: DeleteCustomFieldDropdownOptionConfirmationModal_viewer;
	} & DeleteCustomFieldDropdownOptionConfirmationModalInput
>;

type DeleteCustomFieldDropdownOptionConfirmationModalInput = {
	onConfirm: (replacementOptionId: string | null) => void;
	selectedOption: UnsavedDropdownOption;
	customFieldId: string;
};

const StyledDiv = styled.div`
	margin: 8px 0;
	width: 288px;
`;

export const showDeleteCustomFieldDropdownOptionConfirmationModal = (
	props: DeleteCustomFieldDropdownOptionConfirmationModalInput
): void => {
	showModal({
		type: MODAL_TYPE.DELETE_CUSTOM_FIELD_DROPDOWN_OPTION_CONFIRMATION_MODAL,
		...props,
	});
};

const DeleteCustomFieldDropdownOptionConfirmationModalInternal = ({
	closeModal,
	onConfirm,
	selectedOption: unsavedOption,
	viewer,
}: ModalProps<DeleteCustomFieldDropdownOptionConfirmationModalProps>): JSX.Element => {
	const [replacementOption, setReplacementOption] = useState<DropdownOption | null>(null);

	const selectedOption =
		viewer.customFieldDefinition?.options?.find(option => option.id === unsavedOption.id) ?? unsavedOption;
	const dropdownOptions = viewer.customFieldDefinition?.options?.filter(option => option.id !== selectedOption.id);

	const handleDelete = () => {
		onConfirm(replacementOption?.id || null);
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.delete_dropdown_option" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<Text>
						<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.delete_confirmation_message" />
					</Text>
					<Label>
						<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.display_name" />
					</Label>
					<Text>{selectedOption.displayName}</Text>
					{selectedOption.isUsed && !!dropdownOptions && (
						<>
							<StyledDiv>
								<Separator />
							</StyledDiv>
							<FlexRow>
								<FlexColumn gap={'xs'}>
									<FlexRow>
										<Label size={'2'}>
											<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.replace_option" />
										</Label>
										<Icon icon="help"></Icon>
									</FlexRow>
									<Text color="auxiliary">
										<FormattedMessage
											id="settings.custom-fields.dropdown-options.delete_modal.dropdown_label"
											values={{displayName: selectedOption.displayName}}
										/>
									</Text>
									<FlexRow gap={'l'}>
										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField isDefaultLabel={true}>
													{replacementOption ? (
														<Text>{replacementOption.displayName}</Text>
													) : (
														<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.dropdown_default" />
													)}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content offset={10} useTriggerWidth>
												{dropdownOptions.map(option => (
													<DropdownList.Item onClick={() => setReplacementOption(option)}>
														{option.displayName}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexRow>
								</FlexColumn>
							</FlexRow>
						</>
					)}
					<FlexRow>
						<Modal.PrimaryFooterButton onPress={handleDelete} dataCy="delete">
							<FormattedMessage id="settings.custom-fields.dropdown-options.delete_modal.delete_dropdown_option" />
						</Modal.PrimaryFooterButton>
						<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
							<FormattedMessage id="common.cancel" />
						</Modal.SecondaryFooterButton>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>
		</Modal>
	);
};

export const DeleteCustomFieldDropdownOptionConfirmationModalQuery = graphql`
	query DeleteCustomFieldDropdownOptionConfirmationModalQuery($customFieldId: ID, $hasCustomFieldId: Boolean!) {
		viewer {
			actualPersonId
			component(name: "custom_field_dropdown_options_modal")
			...DeleteCustomFieldDropdownOptionConfirmationModal_viewer
				@arguments(customFieldId: $customFieldId)
				@include(if: $hasCustomFieldId)
		}
	}
`;

export const DeleteCustomFieldDropdownOptionConfirmationModal = createFragmentContainer(
	DeleteCustomFieldDropdownOptionConfirmationModalInternal,
	{
		viewer: graphql`
			fragment DeleteCustomFieldDropdownOptionConfirmationModal_viewer on Viewer
			@argumentDefinitions(customFieldId: {type: "ID"}) {
				customFieldDefinition(id: $customFieldId) {
					... on DropdownCustomField {
						options {
							id
							displayName
							isUsed
						}
					}
				}
			}
		`,
	}
);
